import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import api from '../../utils/api';
import ProductCard from '../../components/common/ProductCard';
import { getPageCache, setPageCache } from '../../utils/pageCache';

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
      <div className="aspect-square bg-gray-100" />
      <div className="p-3 space-y-2">
        <div className="h-3 bg-gray-100 rounded w-1/2" />
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-5 bg-gray-100 rounded w-1/3" />
      </div>
    </div>
  );
}

export default function DealsPage() {
  // Hydrate from cache on Back/Forward for instant paint + seamless scroll restore.
  const cached = getPageCache('deals');
  const [products, setProducts] = useState(cached?.products || []);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    api.get('/flash-deals/active')
      .then(({ data }) => {
        // Each active deal carries its product; dedupe in case a product has
        // multiple deals (e.g. per-variant). ProductCard applies the deal price
        // itself via the activeDeals context.
        const seen = new Set();
        const list = (data.deals || [])
          .map(d => d.product)
          .filter(p => p && !seen.has(p.id) && seen.add(p.id));
        setProducts(list);
        setPageCache('deals', { products: list });
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
        <Link to="/" className="hover:text-brand-yellow">Home</Link>
        <span>›</span>
        <span className="text-brand-dark font-medium">Deals of the Day</span>
      </div>

      {/* Heading */}
      <div className="flex items-center gap-2 mb-5">
        <span className="inline-flex items-center gap-1.5 bg-brand-red text-white text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider">
          <Clock className="w-3.5 h-3.5" /> Limited Time
        </span>
        <h1 className="text-2xl md:text-3xl font-black text-brand-dark">Deals of the Day</h1>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
        {loading
          ? Array.from({ length: 10 }).map((_, i) => <SkeletonCard key={i} />)
          : products.length === 0
            ? (
              <div className="col-span-full text-center py-20">
                <p className="text-5xl mb-4">🏷️</p>
                <p className="text-lg font-bold text-brand-dark mb-2">No active deals right now</p>
                <p className="text-gray-400 text-sm mb-5">Check back soon — new deals drop every day!</p>
                <Link to="/products" className="btn-primary rounded-full px-6 py-2.5 text-sm inline-block">Browse all products</Link>
              </div>
            )
            : products.map(p => <ProductCard key={p.id} product={p} />)
        }
      </div>
    </div>
  );
}
